import React from 'react'
import SafeImage from "../../components/shared/SafeImage";
import MediaFallback from "../../assets/images/dummy-image.png";
import {connect} from 'react-redux';
import moment from 'moment'
import iconVideo from "../../assets/images/selected-scheduler-parlour.svg";
import { FormGroup, CustomInput } from 'reactstrap';
import { withRouter } from 'react-router-dom';
import { updateSchedulerParlour } from '../../api/scheduler-parlour';

// The short name of the ADMIN's own timezone (e.g. "PST", "GMT-5"), derived from
// the browser. Parlour start times are stored in UTC and shown in the admin's
// local zone — this labels them with the *actual* local zone instead of the
// hardcoded "EST" that used to mislabel every non-Eastern admin.
function localTimeZoneAbbrev(date) {
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName');
    return part ? part.value : '';
  } catch (e) {
    return '';
  }
}

// `datetime-local` wants "YYYY-MM-DDTHH:mm" in LOCAL time; the row carries UTC.
function toLocalInputValue(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return moment(d).format('YYYY-MM-DDTHH:mm');
}

function CardParlour({ parlour, loading, updateParlour, getParlourParticipants, handleDeleteParlour, ...props }) {
  const [rescheduling, setRescheduling] = React.useState(false);
  const [newStart, setNewStart] = React.useState('');

  // Staff-side reschedule — asked for by the client ("Add ability for Admin to
  // change the date and time of any scheduled workshops") and missing until now:
  // the PATCH accepted only the two visibility flags. The API delegates to the
  // host's own update path, so reminders are re-enqueued and every RSVP is
  // notified; this does not quietly move the date behind attendees' backs.
  const handlerReschedule = async () => {
    if (!newStart) return;
    try {
      props.setLoading(true);
      const { data } = await props.updateSchedulerParlour(parlour.id, {
        scheduledAt: new Date(newStart).toISOString(),
      });
      updateParlour(data);
      setRescheduling(false);
    } catch (error) {
      console.log(error);
    } finally {
      props.setLoading(false);
    }
  };

  const handlerUpdateParlour = async (key) => {
    try {
      props.setLoading(true);
      const { data } = await props.updateSchedulerParlour(
        parlour.id,
        {
          [key]: !parlour[key],
        }
      );
      updateParlour(data);
    } catch (error) {
      console.log(error);
    } finally {
      props.setLoading(false);
    }
  }
  const handleDownloadVideoIntro = () => {
    if (!parlour.sell_parlour.video_intro_url) return;
    window.open(parlour.sell_parlour.video_intro_url, '_blank');
  };
  return (
    <div className="card-parlour">
      <div className="card-poster">
        {parlour.live && (
          <div className="card-live">
            <div className="card-live-circle" />
            <span className="card-live-text">Live</span>
          </div>
        )}
        <SafeImage fallback={MediaFallback}
          className="card-img"
          src={parlour.poster_url}
          alt={parlour.suite_title}
          loading="lazy"
        />
        <div className="bg-black" />
        <div className="card-info">
          <h3 className="card-title">{parlour.suite_title}</h3>
          <h1 className="card-description">
            '{parlour.description}'
          </h1>
          <span className="card-date">
            {moment(parlour.start_date).local().format("MMM DD, h:mma")}{" "}
            {localTimeZoneAbbrev(new Date(parlour.start_date))}
          </span>
        </div>
        <div className="parlour-video">
          <img
            className="parlour-icon_video"
            src={iconVideo}
            alt="video_icon"
            loading="lazy"
          />
        </div>
      </div>
      <div className="card-participants">
        <div className="card-participant">
          <h3 className="card-participant_title">Host:</h3>
          <div className="card-participant_detail">
            <SafeImage
              className="card-participant_photo"
              src={parlour.owner.profile_image_url}
              alt={parlour.owner.name}
              loading="lazy"
            />
            <span className="card-participant_name">
              {parlour.owner.name}
            </span>
          </div>
        </div>
        {parlour.guest && (
          <div className="card-participant">
            <h3 className="card-participant_title">Guest:</h3>
            <div className="card-participant_detail">
              <SafeImage
                className="card-participant_photo guest"
                src={parlour.guest.profile_image_url}
                alt={parlour.guest.name}
                loading="lazy"
              />
              <span className="card-participant_name">
                {parlour.guest.name}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="card-actions">
        <FormGroup className="customswitch">
          <CustomInput
            type="switch"
            id={`switch-parlour-${parlour.id}`}
            name={`switch-parlour-${parlour.id}`}
            value={parlour.is_public}
            checked={parlour.is_public}
            onChange={() => handlerUpdateParlour('is_public')}
            label="Publish in the App?"
          />
        </FormGroup>
        {
          // `community_tab` is a write-only column: nothing in the API or the app
          // ever reads it (Discover is gated by `is_public` alone). The label
          // used to claim it controlled Discover visibility, so staff toggled it
          // expecting a change and got none. Kept — 247 of 281 QA rows are false,
          // so wiring it as a read filter would hide most of the catalogue — but
          // now labelled for what it is.
          !parlour.live && (
            <FormGroup className="customswitch">
              <CustomInput
                type="switch"
                id={`switch-community-${parlour.id}`}
                name={`switch-community-${parlour.id}`}
                value={parlour.community_tab}
                checked={parlour.community_tab}
                onChange={() => handlerUpdateParlour('community_tab')}
                label="Community tab flag (not read by the app)"
              />
            </FormGroup>
          )
        }
        {!parlour.live && !rescheduling && (
          <button
            disabled={loading}
            className="btn btn-primary"
            onClick={() => {
              setNewStart(toLocalInputValue(parlour.start_date));
              setRescheduling(true);
            }}
          >
            Change Date &amp; Time
          </button>
        )}
        {rescheduling && (
          <div className="w-100 mb-2">
            <input
              type="datetime-local"
              className="form-control mb-2"
              value={newStart}
              onChange={(e) => setNewStart(e.target.value)}
            />
            <small className="d-block mb-2 text-muted">
              Everyone who RSVP'd is notified and the 24h / 1h / 15m reminders move with it.
            </small>
            <button
              disabled={loading || !newStart}
              className="btn btn-success mr-2"
              onClick={handlerReschedule}
            >
              Save
            </button>
            <button
              disabled={loading}
              className="btn btn-secondary"
              onClick={() => setRescheduling(false)}
            >
              Cancel
            </button>
          </div>
        )}
        <button
          disabled={loading}
          className="btn btn-primary"
          onClick={() => getParlourParticipants(parlour.id)}
        >
          All RSVP
        </button>
        <button
          disabled={loading}
          className="btn delete-parlour"
          onClick={() => handleDeleteParlour(parlour.id)}
        >
          Delete Parlour
        </button>
        <div className='d-flex justify-content-center w-100'>
          <button
            disabled={loading || !parlour.sell_parlour.video_intro_url}
            className={parlour.sell_parlour.video_intro_url ? "btn-success" : "btn-default"}
            onClick={() => handleDownloadVideoIntro()}
          >
            Download Video Intro
          </button>
        </div>
      </div>
    </div>
  )
}

export default connect(null, {
  updateSchedulerParlour,
})(withRouter(CardParlour));